export type AppRouteId = "home" | "gallery" | "delegation" | "transfers" | "ens" | "settings";
export type OutsideSafeRouteAccess = "allowlisted" | "blocked";
export const SAFE_TRACKING_QUERY_PARAM_KEYS = ["utm_source", "safe_ref"] as const;
export const SAFE_WALLET_UTM_SOURCE = "SafeWallet";

export type SafeTrackingQueryParams = {
  utmSource?: typeof SAFE_WALLET_UTM_SOURCE;
  safeRef?: string;
};

type AppRouteLabelKey =
  "nav.home" | "nav.gallery" | "nav.delegation" | "nav.transfers" | "nav.ens" | "nav.settings";

export type AppRouteDefinition = {
  id: AppRouteId;
  pathSegment: "" | "gallery" | "delegation" | "transfers" | "ens" | "settings";
  href: "/" | "/gallery" | "/delegation" | "/transfers" | "/ens" | "/settings";
  navLabelKey: AppRouteLabelKey;
  outsideSafeReadOnlyAccess: OutsideSafeRouteAccess;
  showInPrimaryNav?: boolean;
  // Extra pathnames that keep this nav item highlighted (routes it owns that
  // live outside its own href, e.g. Delegation owns /activity).
  alsoActiveHrefs?: readonly string[];
};

export type OutsideSafeReadOnlyHref = AppRouteDefinition["href"] | "/activity";

export const APP_ROUTE_DEFINITIONS: ReadonlyArray<AppRouteDefinition> = [
  {
    id: "home",
    pathSegment: "",
    href: "/",
    navLabelKey: "nav.home",
    outsideSafeReadOnlyAccess: "allowlisted"
  },
  {
    id: "gallery",
    pathSegment: "gallery",
    href: "/gallery",
    navLabelKey: "nav.gallery",
    outsideSafeReadOnlyAccess: "allowlisted"
  },
  {
    id: "transfers",
    pathSegment: "transfers",
    href: "/transfers",
    navLabelKey: "nav.transfers",
    outsideSafeReadOnlyAccess: "allowlisted"
  },
  {
    id: "delegation",
    pathSegment: "delegation",
    href: "/delegation",
    navLabelKey: "nav.delegation",
    outsideSafeReadOnlyAccess: "allowlisted",
    alsoActiveHrefs: ["/activity"]
  },
  {
    id: "ens",
    pathSegment: "ens",
    href: "/ens",
    navLabelKey: "nav.ens",
    outsideSafeReadOnlyAccess: "allowlisted"
  },
  {
    id: "settings",
    pathSegment: "settings",
    href: "/settings",
    navLabelKey: "nav.settings",
    outsideSafeReadOnlyAccess: "allowlisted"
  }
];

export const OUTSIDE_SAFE_READ_ONLY_EXTRA_ALLOWLISTED_HREFS = ["/activity"] as const;

export const OUTSIDE_SAFE_READ_ONLY_ALLOWLISTED_HREFS: ReadonlyArray<OutsideSafeReadOnlyHref> = [
  ...APP_ROUTE_DEFINITIONS.filter((route) => route.outsideSafeReadOnlyAccess === "allowlisted").map(
    (route) => route.href
  ),
  ...OUTSIDE_SAFE_READ_ONLY_EXTRA_ALLOWLISTED_HREFS
];

export const OUTSIDE_SAFE_READ_ONLY_BLOCKED_HREFS: ReadonlyArray<AppRouteDefinition["href"]> =
  APP_ROUTE_DEFINITIONS.filter((route) => route.outsideSafeReadOnlyAccess === "blocked").map(
    (route) => route.href
  );

const OUTSIDE_SAFE_READ_ONLY_ALLOWLISTED_HREF_SET = new Set<string>(
  OUTSIDE_SAFE_READ_ONLY_ALLOWLISTED_HREFS
);

export function isOutsideSafeReadOnlyAllowlistedHref(pathname: string): boolean {
  return OUTSIDE_SAFE_READ_ONLY_ALLOWLISTED_HREF_SET.has(pathname);
}

function normalizeSafeRefValue(rawSafeRefValue: string | null): string | undefined {
  if (!rawSafeRefValue) {
    return undefined;
  }

  const trimmedSafeRefValue = rawSafeRefValue.trim();
  return trimmedSafeRefValue.length > 0 ? trimmedSafeRefValue : undefined;
}

export function extractSafeTrackingQueryParams(
  ...searchParamSources: ReadonlyArray<URLSearchParams>
): SafeTrackingQueryParams {
  const trackingQueryParams: SafeTrackingQueryParams = {};

  for (const searchParams of searchParamSources) {
    if (!trackingQueryParams.utmSource) {
      const utmSourceValue = searchParams.get("utm_source");
      if (utmSourceValue === SAFE_WALLET_UTM_SOURCE) {
        trackingQueryParams.utmSource = SAFE_WALLET_UTM_SOURCE;
      }
    }

    if (!trackingQueryParams.safeRef) {
      const normalizedSafeRefValue = normalizeSafeRefValue(searchParams.get("safe_ref"));
      if (normalizedSafeRefValue) {
        trackingQueryParams.safeRef = normalizedSafeRefValue;
      }
    }
  }

  return trackingQueryParams;
}

export function applySafeTrackingQueryParams(
  searchParams: URLSearchParams,
  trackingQueryParams: SafeTrackingQueryParams
): void {
  if (trackingQueryParams.utmSource === SAFE_WALLET_UTM_SOURCE) {
    searchParams.set("utm_source", SAFE_WALLET_UTM_SOURCE);
  }

  if (trackingQueryParams.safeRef) {
    searchParams.set("safe_ref", trackingQueryParams.safeRef);
  }
}
